import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Dropdown,
  DropdownButton,
  Row,
  Table,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  HeaderDisplayKeysWithType,
} from '../../../components';
import { useSemGrade } from '../../../hook';
import { ClassSightRate, UserInfo } from '../../../model';
import apis from '../../../apis';
import {
  downloadDataAsExcel,
  printDataAsTable,
  SheetHeaderDisplay,
} from '../../../utils';
import { toast } from 'react-toastify';

const mapState = (app: ApplicationState) => ({
  ...app.auth,
});

const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

type ExportRow = {
  grade: string;
  className: string;
  countD: number;
  countN: number;
  returnRate: string;
  rowType: 'class' | 'gradeTotal' | 'schoolTotal';
};

const exportHeaders: (
  SheetHeaderDisplay<ExportRow> &
  HeaderDisplayKeysWithType<ExportRow>
)[] = [
    {
      display: '年級',
      property: 'grade',
    },
    {
      display: '班級',
      property: 'className',
    },
    {
      display: '裸眼視力不良人數B',
      property: 'countD',
    },
    {
      display: '經散瞳確認近視的人數A',
      property: 'countN',
    },
    {
      display: '經散瞳確認近視比率(A/B)',
      property: 'returnRate',
    },
  ];

const sightDilatingNearRate: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const { yearSem, element } = useSemGrade();

  const [datas, setDatas] = useState<ClassSightRate[]>([]);
  const [userinfo, setUserinfo] = useState<UserInfo>();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!yearSem) return;

    toast
      .promise(
        apis
          .getSightDilatingNearRate(
            yearSem.year,
            yearSem.sem
          )
          .then((result) => {
            setDatas(result);
          }),
        {
          pending: '資料載入中......',
          success: '資料載入成功！',
          error: '資料載入失敗！請查看錯誤資訊。',
        }
      )
      .catch(catchErrorForModal);

    apis
      .getUserInfo()
      .then((result) => {
        setUserinfo(result);
      })
      .catch(catchErrorForModal);
  }, [
    yearSem?.year,
    yearSem?.sem,
    catchErrorForModal,
  ]);

  const exportRows = useMemo<ExportRow[]>(() => {
    const result: ExportRow[] = [];

    const groupedDatas = datas.reduce(
      (groups, data) => {
        if (!groups[data.grade]) {
          groups[data.grade] = [];
        }

        groups[data.grade].push(data);

        return groups;
      },
      {} as Record<number, ClassSightRate[]>
    );

    let schoolCountD = 0;
    let schoolCountN = 0;

    Object.entries(groupedDatas)
      .sort(
        ([gradeA], [gradeB]) =>
          Number(gradeA) - Number(gradeB)
      )
      .forEach(([grade, gradeDatas]) => {
        let gradeCountD = 0;
        let gradeCountN = 0;

        gradeDatas
          .slice()
          .sort((a, b) => {
            return String(a.className).localeCompare(
              String(b.className),
              'zh-Hant',
              {
                numeric: true,
              }
            );
          })
          .forEach((data) => {
            gradeCountD += data.countD;
            gradeCountN += data.countN;

            schoolCountD += data.countD;
            schoolCountN += data.countN;

            result.push({
              grade,
              className: String(data.className),
              countD: data.countD,
              countN: data.countN,
              returnRate: percent(
                data.countN,
                data.countD
              ),
              rowType: 'class',
            });
          });

        result.push({
          grade: `${grade}年級小計`,
          className: '',
          countD: gradeCountD,
          countN: gradeCountN,
          returnRate: percent(
            gradeCountN,
            gradeCountD
          ),
          rowType: 'gradeTotal',
        });
      });

    result.push({
      grade: '全校合計',
      className: '',
      countD: schoolCountD,
      countN: schoolCountN,
      returnRate: percent(
        schoolCountN,
        schoolCountD
      ),
      rowType: 'schoolTotal',
    });

    return result;
  }, [datas]);

  const title =
    `${user.schools[user.currentSchool].name}` +
    `${yearSem?.year ?? ''}學年第` +
    `${yearSem?.sem ?? ''}學期_` +
    '經散瞳確認近視比率';

  const footer = '承辦人: 組長: 主任: 校長:';

  const handleExport = async (
    type: 'excel' | 'print'
  ) => {
    if (!exportRows.length || isExporting) return;

    setIsExporting(true);

    try {
      await toast.promise(
        new Promise<void>((resolve, reject) => {
          window.setTimeout(() => {
            try {
              if (type === 'excel') {
                downloadDataAsExcel({
                  title,
                  values: exportRows,
                  headers: exportHeaders,
                  footer,
                  exportfile:
                    userinfo?.exportFileType,
                  orientation: 'portrait',
                });
              } else {
                printDataAsTable({
                  title,
                  values: exportRows,
                  headers: exportHeaders,
                  footer,
                  orientation: 'portrait',
                });
              }

              resolve();
            } catch (error) {
              reject(error);
            }
          }, 100);
        }),
        {
          pending:
            type === 'excel'
              ? 'Excel 資料準備中，請稍候......'
              : '列印資料準備中，請稍候......',
          success:
            type === 'excel'
              ? 'Excel 資料準備完成！'
              : '列印資料準備完成！',
          error: '資料處理失敗！請查看錯誤資訊。',
        }
      );
    } catch (error) {
      catchErrorForModal(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AuthedLayout>
      <Row className="justify-content-between">
        <span className="mr-3">{element}</span>

        <DropdownButton
          id="sight-dilating-near-rate-export"
          title={
            isExporting ? '資料處理中...' : '匯出'
          }
          variant="success"
          disabled={
            !exportRows.length || isExporting
          }
        >
          <Dropdown.Item
            onClick={() => handleExport('excel')}
          >
            Excel 下載
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => handleExport('print')}
          >
            列印
          </Dropdown.Item>
        </DropdownButton>
      </Row>

      <hr />

      <Row>
        <Table
          striped
          bordered
          className="text-center"
          style={{
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              {exportHeaders.map((header) => (
                <th key={String(header.property)}>
                  {header.display}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {exportRows.map((row, index) => (
              <tr
                key={`${row.grade}-${row.className}-${index}`}
                className={
                  row.rowType !== 'class'
                    ? 'font-weight-bold'
                    : ''
                }
              >
                <td>{row.grade}</td>
                <td>{row.className}</td>
                <td>{row.countD}</td>
                <td>{row.countN}</td>
                <td>{row.returnRate}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Row>
    </AuthedLayout>
  );
};

function percent(
  value: number,
  denominator: number
): string {
  if (!denominator) return '0.00%';

  return (
    ((value / denominator) * 100).toFixed(2) +
    '%'
  );
}

export const SightDilatingNearRate = connector(sightDilatingNearRate);